var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './SingleDatePicker.module.css';
import { Calendar, FilterDropdown, MonthPicker } from '..';
import { classNames, convertToSiteTz, genericDateFormat } from '../../utils';
import { DateTime } from 'luxon';
const SingleDatePicker = (_a) => {
    var { value, disableBefore, disableAfter, setDate, label, alignItemsRight, disabled, locale, className, ianaTimeZoneCode, openedeFromPopup } = _a, props = __rest(_a, ["value", "disableBefore", "disableAfter", "setDate", "label", "alignItemsRight", "disabled", "locale", "className", "ianaTimeZoneCode", "openedeFromPopup"]);
    const { dateFormat } = locale;
    const selectionToDisplayString = useCallback((ds) => genericDateFormat(DateTime.fromISO(ds, { zone: ianaTimeZoneCode }).toJSDate(), dateFormat, ianaTimeZoneCode), [dateFormat, ianaTimeZoneCode]);
    const dateToSelection = useCallback((d) => genericDateFormat(d, 'yyyy-MM-dd', ianaTimeZoneCode), []);
    const initialSelection = useMemo(() => value
        ? {
            date: value,
            isTentative: false,
        }
        : undefined, [value]);
    const now = convertToSiteTz(new Date(), ianaTimeZoneCode);
    const today = {
        day: now.getDate(),
        month: now.getMonth(),
        year: now.getFullYear(),
        selection: dateToSelection(now),
    };
    const [selection, setSelection] = useState(initialSelection);
    const [displayedYear, displayAnotherYear] = useState(null);
    const [externalControlOpen, dropdown] = useState(false);
    const [title, setTitle] = useState(dateFormat.toUpperCase());
    const externalControlSetOpen = useCallback((tf) => {
        displayAnotherYear(null);
        if (tf === false)
            setSelection(initialSelection);
        dropdown(tf);
    }, [initialSelection]);
    const selectedYear = useMemo(() => {
        if (!selection)
            return;
        return Number(selection.date.split('-')[0]);
    }, [selection]);
    const selectedMonthIndex = useMemo(() => {
        if (!selection)
            return;
        return Number(selection.date.split('-')[1]) - 1;
    }, [selection]);
    const selectionOk = selection && !selection.isTentative;
    // reset selection when value changed, especially when picker is reusable
    useEffect(() => {
        setSelection(initialSelection);
    }, [value]);
    useEffect(() => {
        if (!selection)
            setTitle(dateFormat.toUpperCase());
        if (selectionOk)
            setTitle(selectionToDisplayString(selection.date));
    }, [selection, selectionOk, dateFormat, selectionToDisplayString]);
    const displayedTitle = label ? (_jsxs("div", { children: [_jsx("div", Object.assign({ className: styles.label }, { children: label }), void 0), title] }, void 0)) : (title);
    const year = displayedYear || selectedYear || today.year;
    return (_jsx(FilterDropdown, Object.assign({ buttonClassName: classNames(styles.filterButton, className), dropdownClassName: openedeFromPopup ? styles.dropdownWrapperInModal : styles.dropdownWrapper, title: displayedTitle, alignItemsRight: alignItemsRight, active: selectionOk, disabled: disabled, roundedCorners: true }, props, { externalControlOpen: externalControlOpen, externalControlSetOpen: externalControlSetOpen }, { children: selection && selection.isTentative ? (_jsx(Calendar, { isSingleDay: true, displayMonthOnly: selection.date !== (initialSelection === null || initialSelection === void 0 ? void 0 : initialSelection.date), selectedSingleDay: selection.date, onChange: (d) => {
                setDate(d);
                setSelection({
                    date: d,
                    isTentative: false,
                });
                dropdown(false);
            }, disableBefore: disableBefore, disableAfter: disableAfter || today.selection }, void 0)) : (_jsx("div", Object.assign({ className: styles.pickerPadded }, { children: _jsx(MonthPicker, { disableBefore: disableBefore, disableAfter: disableAfter || today.selection, selectedMonthIndex: selectedMonthIndex !== undefined
                    ? selectedMonthIndex
                    : today.month, setSelectedMonthIndex: (month) => {
                    const monthString = `${month + 1}`.padStart(2, '0');
                    const yearMonthString = `${year}-${monthString}`;
                    const date = (initialSelection === null || initialSelection === void 0 ? void 0 : initialSelection.date.startsWith(yearMonthString) // same month
                    )
                        ? initialSelection.date
                        : `${yearMonthString}-01`;
                    setSelection({
                        date,
                        isTentative: true,
                    });
                    displayAnotherYear(null);
                }, selectedYear: year, setSelectedYear: displayAnotherYear, ianaTimeZoneCode: ianaTimeZoneCode }, void 0) }), void 0)) }), void 0));
};
export default SingleDatePicker;
